import React, { useEffect, useState } from 'react'
import { Button as ButtonComponent } from '@/components/ui/button'
import {
    Dialog as DialogComponent,
    DialogContent as DialogContentComponent,
    DialogDescription as DialogDescriptionComponent,
    DialogFooter as DialogFooterComponent,
    DialogHeader as DialogHeaderComponent,
    DialogTitle as DialogTitleComponent,
} from '@/components/ui/dialog'
import { Label as LabelComponent } from '@/components/ui/label'
import {
    RadioGroup as RadioGroupComponent,
    RadioGroupItem as RadioGroupItemComponent,
} from '@/components/ui/radio-group'

export const CANCEL_CHOICES = Object.freeze({
    RESUME: 'resume_warmup',
    LEAVE_PAUSED: 'leave_paused',
})

const Button = ButtonComponent as React.ComponentType<any>
const Dialog = DialogComponent as React.ComponentType<any>
const DialogContent = DialogContentComponent as React.ComponentType<any>
const DialogDescription = DialogDescriptionComponent as React.ComponentType<any>
const DialogFooter = DialogFooterComponent as React.ComponentType<any>
const DialogHeader = DialogHeaderComponent as React.ComponentType<any>
const DialogTitle = DialogTitleComponent as React.ComponentType<any>
const Label = LabelComponent as React.ComponentType<any>
const RadioGroup = RadioGroupComponent as React.ComponentType<any>
const RadioGroupItem = RadioGroupItemComponent as React.ComponentType<any>

interface WarmupCancelDialogProps {
    open: boolean
    /** The expansion paused the warm-up ad set, so the owner must say what happens to it. */
    choiceRequired: boolean
    processing: boolean
    onClose: () => void
    onConfirm: (choice: string | null) => void
}

/**
 * Cancelling a warm-up. When the warm-up ad set was paused for the expansion the owner has to choose what happens to
 * it; there is no default choice, so nothing is switched on or left paused unless they say so.
 */
const WarmupCancelDialog: React.FC<WarmupCancelDialogProps> = ({
    open,
    choiceRequired,
    processing,
    onClose,
    onConfirm,
}) => {
    const [choice, setChoice] = useState<string>('')

    // No choice is remembered: every opening starts without one
    useEffect(() => {
        if (open) setChoice('')
    }, [open])

    return (
        <Dialog open={open} onOpenChange={(next: boolean) => !next && onClose()}>
            <DialogContent className="tw:!w-lg">
                <DialogHeader>
                    <DialogTitle>Cancel the warm-up</DialogTitle>
                </DialogHeader>
                <DialogDescription>
                    Audience ad sets that were not created on Meta yet are removed. Ad sets that are already running are
                    not changed.
                </DialogDescription>
                {choiceRequired && (
                    <RadioGroup
                        value={choice}
                        onValueChange={setChoice}
                        aria-label="What happens to the warm-up ad set"
                        className="tw:flex tw:flex-col tw:gap-3"
                    >
                        <div className="tw:flex tw:items-start tw:gap-3">
                            <RadioGroupItem value={CANCEL_CHOICES.RESUME} id="cancel-choice-resume" />
                            <Label htmlFor="cancel-choice-resume" className="tw:cursor-pointer">
                                Switch the warm-up ad set on again
                            </Label>
                        </div>
                        <div className="tw:flex tw:items-start tw:gap-3">
                            <RadioGroupItem value={CANCEL_CHOICES.LEAVE_PAUSED} id="cancel-choice-leave" />
                            <Label htmlFor="cancel-choice-leave" className="tw:cursor-pointer">
                                Leave everything paused
                            </Label>
                        </div>
                    </RadioGroup>
                )}
                <DialogFooter className="tw:gap-4 tw:py-4">
                    <Button variant="secondary" onClick={onClose} disabled={processing}>
                        Keep the warm-up
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={() => onConfirm(choiceRequired ? choice : null)}
                        disabled={processing || (choiceRequired && !choice)}
                    >
                        Cancel the warm-up
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

export default WarmupCancelDialog
